import React, { useState, useEffect, useCallback } from 'react';
import { anomalyApi, satelliteBoundariesApi } from '../../services/api';
import Modal from '../../components/common/Modal';

type Severity = 'low' | 'medium' | 'high' | 'critical';
type AnomalyStatus = 'pending' | 'investigating' | 'resolved' | 'false_positive';

interface Anomaly {
  id: string;
  boundary_name: string;
  boundary_id: string;
  anomaly_type: string;
  severity: Severity;
  status: AnomalyStatus;
  change_percentage: number | string;
  baseline_ndvi?: number | string;
  current_ndvi?: number;
  affected_area_hectares?: number;
  description?: string;
  detection_date: string;
  resolution_notes?: string;
}

interface AnomalyStats {
  total: number;
  bySeverity: Record<string, number>;
  byStatus: Record<string, number>;
  recentTrend: number;
}

const SEVERITY_STYLE: Record<Severity, string> = {
  low:      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  medium:   'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  high:     'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  critical: 'bg-red-200 text-red-900 font-bold dark:bg-red-900/60 dark:text-red-200',
};

const STATUS_STYLE: Record<AnomalyStatus, string> = {
  pending:        'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  investigating:  'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  resolved:       'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  false_positive: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
};

const STATUS_LABELS: Record<AnomalyStatus, string> = {
  pending:        'Pending',
  investigating:  'Investigating',
  resolved:       'Resolved',
  false_positive: 'False Positive',
};

const SEVERITY_DESC: Record<Severity, string> = {
  low:      'NDVI change < 10% — monitor',
  medium:   'NDVI change 10–20% — attention needed',
  high:     'NDVI change 20–30% — investigate soon',
  critical: 'NDVI change > 30% — immediate action',
};

// Small inline tooltip
function HelpTip({ text }: { text: string }) {
  return (
    <span
      title={text}
      className="inline-flex items-center justify-center w-4 h-4 ml-1 rounded-full bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-300 text-xs font-bold cursor-help select-none"
    >?</span>
  );
}

export const AnomalyDashboardPage: React.FC = () => {
  const [anomalies, setAnomalies]     = useState<Anomaly[]>([]);
  const [stats, setStats]             = useState<AnomalyStats | null>(null);
  const [boundaries, setBoundaries]   = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading]         = useState(true);
  const [monitoring, setMonitoring]   = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [success, setSuccess]         = useState<string | null>(null);

  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterStatus, setFilterStatus]     = useState('');
  const [filterBoundary, setFilterBoundary] = useState('');

  const [updating, setUpdating]     = useState<string | null>(null);
  const [newStatus, setNewStatus]   = useState<AnomalyStatus>('investigating');
  const [notes, setNotes]           = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [anomalyRes, statsRes] = await Promise.all([
        anomalyApi.getAnomalies({
          severity:   filterSeverity || undefined,
          status:     filterStatus   || undefined,
          boundaryId: filterBoundary || undefined,
        }),
        anomalyApi.getStats(),
      ]);
      setAnomalies(anomalyRes.anomalies || []);
      setStats(statsRes);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.response?.data?.message || err.message || 'Failed to load anomalies');
    } finally { setLoading(false); }
  }, [filterSeverity, filterStatus, filterBoundary]);

  useEffect(() => {
    loadData();
    satelliteBoundariesApi.getAll()
      .then((res) => setBoundaries(res.data?.boundaries || res.boundaries || []))
      .catch(() => {});
  }, [loadData]);

  const handleRunMonitoring = async () => {
    setMonitoring(true); setError(null);
    try {
      const res = await anomalyApi.runMonitoring();
      setSuccess(`✅ Monitoring complete — ${res.anomaliesDetected} anomaly(s) detected across all active boundaries`);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.response?.data?.message || err.message || 'Monitoring failed');
    } finally { setMonitoring(false); }
  };

  const submitStatusUpdate = async () => {
    if (!updating) return;
    try {
      await anomalyApi.updateStatus(updating, { status: newStatus, notes: notes || undefined });
      setSuccess('Status updated');
      setUpdating(null);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to update status');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this anomaly record?')) return;
    try {
      await anomalyApi.deleteAnomaly(id);
      setAnomalies((prev) => prev.filter((a) => a.id !== id));
      setSuccess('Anomaly deleted');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to delete anomaly');
    }
  };

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Anomaly Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Vegetation change alerts generated by Google Earth Engine — review, investigate, and resolve
          </p>
        </div>
        <button onClick={handleRunMonitoring} disabled={monitoring}
          title="Runs GEE change detection for every active boundary in your organisation and stores any new anomalies"
          className="px-5 py-2 bg-orange-600 text-white text-sm font-medium rounded-md hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2">
          {monitoring && <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>}
          {monitoring ? 'Running GEE monitoring…' : '🤖 Run Automated Monitoring'}
        </button>
      </div>

      {/* ── How it works banner ── */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-sm">
        <p className="font-semibold text-blue-700 dark:text-blue-300 mb-1">How anomalies are created</p>
        <ol className="list-decimal list-inside space-y-0.5 text-blue-600 dark:text-blue-400 text-xs">
          <li>GEE computes median NDVI over a <strong>baseline period</strong> (older) and a <strong>current period</strong> (recent)</li>
          <li>If NDVI changed by more than <strong>15%</strong>, an anomaly is recorded here</li>
          <li>You can also trigger detection per-boundary from the <strong>Imagery &amp; Indices</strong> page</li>
        </ol>
        <p className="text-xs text-blue-500 dark:text-blue-400 mt-2">
          💡 <strong>Automated Monitoring</strong> runs detection for all active boundaries at once.
          Use filters to focus on critical or unreviewed items.
        </p>
      </div>

      {/* ── Alerts ── */}
      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 p-4 flex justify-between items-start">
          <p className="text-sm text-red-800">{error}</p>
          <button onClick={() => setError(null)} className="text-red-500 ml-4 text-lg leading-none">&times;</button>
        </div>
      )}
      {success && (
        <div className="rounded-md bg-green-50 border border-green-200 p-4 flex justify-between items-start">
          <p className="text-sm text-green-800">{success}</p>
          <button onClick={() => setSuccess(null)} className="text-green-500 ml-4 text-lg leading-none">&times;</button>
        </div>
      )}

      {/* ── Stats cards ── */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Anomalies', value: stats.total,                    color: 'text-gray-900 dark:text-white',           tip: 'All anomalies ever recorded for your organisation' },
            { label: 'Critical',        value: stats.bySeverity?.critical || 0, color: 'text-red-700 dark:text-red-400',          tip: SEVERITY_DESC.critical },
            { label: 'High',            value: stats.bySeverity?.high     || 0, color: 'text-orange-700 dark:text-orange-400',    tip: SEVERITY_DESC.high },
            { label: 'Pending Review',  value: stats.byStatus?.pending    || 0, color: 'text-yellow-700 dark:text-yellow-400',    tip: 'Anomalies that have not yet been investigated or resolved' },
          ].map(({ label, value, color, tip }) => (
            <div key={label} title={tip} className="bg-white dark:bg-gray-800 rounded-lg shadow p-5 cursor-help">
              <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
              <p className={`text-3xl font-bold mt-1 ${color}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── Severity legend ── */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Severity Reference</p>
        <div className="flex flex-wrap gap-3">
          {(Object.entries(SEVERITY_DESC) as [Severity, string][]).map(([sev, desc]) => (
            <div key={sev} className="flex items-center gap-2">
              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium capitalize ${SEVERITY_STYLE[sev]}`}>{sev}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{desc}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Filter Anomalies</p>
        <div className="flex flex-wrap gap-3 items-center">
          <select value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)}
            title="Filter by severity level"
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            title="Filter by investigation status"
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="investigating">Investigating</option>
            <option value="resolved">Resolved</option>
            <option value="false_positive">False Positive</option>
          </select>

          <select value={filterBoundary} onChange={(e) => setFilterBoundary(e.target.value)}
            title="Filter by monitoring boundary"
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">All Boundaries</option>
            {boundaries.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>

          <button onClick={loadData}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700">
            Apply Filters
          </button>

          {(filterSeverity || filterStatus || filterBoundary) && (
            <button onClick={() => { setFilterSeverity(''); setFilterStatus(''); setFilterBoundary(''); }}
              className="px-3 py-2 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline">
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* ── Anomaly table ── */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {anomalies.length} anomaly{anomalies.length !== 1 ? 's' : ''} {filterSeverity || filterStatus || filterBoundary ? '(filtered)' : ''}
          </p>
          <p className="text-xs text-gray-400">Click <strong>Update</strong> on any row to change its investigation status</p>
        </div>

        {loading ? (
          <div className="p-10 flex justify-center">
            <svg className="animate-spin h-8 w-8 text-blue-500" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
          </div>
        ) : anomalies.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-4xl mb-2">🔍</p>
            <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">No anomalies found</p>
            <p className="text-gray-400 text-xs mt-1">
              Run <strong>Automated Monitoring</strong> above, or use <strong>Run Change Detection</strong> on the Imagery page
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Boundary</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Severity <HelpTip text="Based on percentage NDVI change between baseline and current period" />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    NDVI Change <HelpTip text="Negative = vegetation declined. Positive = vegetation increased." />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Detected</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status <HelpTip text="pending → investigating → resolved / false_positive" />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {anomalies.map((a) => (
                  <tr key={a.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{a.boundary_name}</td>
                    <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">{a.anomaly_type?.replace(/_/g, ' ')}</td>
                    <td className="px-4 py-3">
                      <span title={SEVERITY_DESC[a.severity]}
                        className={`inline-flex px-2 py-0.5 rounded text-xs font-medium capitalize cursor-help ${SEVERITY_STYLE[a.severity]}`}>
                        {a.severity}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono">
                      {a.change_percentage != null ? (
                        <span className={Number(a.change_percentage) < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>
                          {Number(a.change_percentage) > 0 ? '+' : ''}{Number(a.change_percentage).toFixed(1)}%
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {a.detection_date
                        ? new Date(a.detection_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLE[a.status]}`}>
                        {STATUS_LABELS[a.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => { setUpdating(a.id); setNewStatus(a.status); setNotes(''); }}
                          title="Update investigation status and add notes"
                          className="text-xs px-2 py-1 border border-blue-300 rounded text-blue-700 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-400">
                          Update
                        </button>
                        <button onClick={() => handleDelete(a.id)}
                          title="Delete this anomaly record permanently"
                          className="text-xs px-2 py-1 border border-red-200 rounded text-red-600 hover:bg-red-50">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Status update modal ── */}
      {updating && (
        <Modal onClose={() => setUpdating(null)} panelClassName="max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Update Anomaly Status</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Track your investigation progress. Add notes to document findings or reason for closing.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  New Status
                  <HelpTip text="pending = not started · investigating = in progress · resolved = confirmed and handled · false_positive = no real issue" />
                </label>
                <select value={newStatus} onChange={(e) => setNewStatus(e.target.value as AnomalyStatus)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm">
                  <option value="pending">Pending — not yet reviewed</option>
                  <option value="investigating">Investigating — currently reviewing</option>
                  <option value="resolved">Resolved — issue confirmed and handled</option>
                  <option value="false_positive">False Positive — no real issue</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes (optional)</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                  placeholder="e.g. Visited site — confirmed seasonal crop rotation, not vegetation loss…"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm" />
              </div>
              <div className="flex gap-3">
                <button onClick={submitStatusUpdate}
                  className="flex-1 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700">
                  Save
                </button>
                <button onClick={() => setUpdating(null)}
                  className="flex-1 py-2 border border-gray-300 dark:border-gray-600 text-sm rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white">
                  Cancel
                </button>
              </div>
            </div>
        </Modal>
      )}
    </div>
  );
};

// Made with Bob
